import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Pagination,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'

import theme from 'src/theme'
import React, { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  PreviewIcon,
  SubmitIcon,
} from 'src/v3/core/icons'
import ConfirmAction from 'src/v3/components/confirm-action'
import EntityList from 'src/components/common/entity-list'
import CourseCard from 'src/components/common/course-card'
import {
  fetchSpecialCertifications,
  selectSpecialCertifications,
  selectSpecialCertificationsStatus,
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
} from './admin.slice'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let specialCertifications = useAppSelector(selectSpecialCertifications)
  let specialCertificationsStatus = useAppSelector(
    selectSpecialCertificationsStatus,
  )
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let [saving, setSaving] = useState<boolean>(false)
  let [selected, setSelected] = useState<string>('')
  let [action, setAction] = useState<'create' | 'view' | 'edit' | 'delete'>(
    'view',
  )
  let [checkedCourses, setCheckedCourses] = useState<string[]>([])
  let [page, setPage] = useState<number>(1)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let selectedCert = specialCertifications.find((sc) => sc.id === selected)

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/admin/special-certification', {
            name: values.name,
            courses: checkedCourses,
          })
          .then(() => dispatch(fetchSpecialCertifications()))
          .then(() => setAction('view'))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(
              err?.message || 'Error Creating Special Certification',
            )
            dispatch(fetchSpecialCertifications())
            setAction('view')
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/admin/special-certification', {
            id: values.id,
            name: values.name,
            courses: checkedCourses,
          })
          .then(() => dispatch(fetchSpecialCertifications()))
          .then(() => setAction('view'))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(
              err?.message || 'Error Updating Special Certification',
            )
            dispatch(fetchSpecialCertifications())
            setAction('view')
            setSaving(false)
          })
      }
    },
  })

  let onSelection = (status: typeof action, id: string) => {
    let selectedSpecialCert = specialCertifications.find((sc) => sc.id === id)
    form.setFieldValue('id', selectedSpecialCert?.id || '')
    form.setFieldValue('name', selectedSpecialCert?.name || '')
    setCheckedCourses(selectedSpecialCert?.courseIDs || [])
    setSelected(id)
    setAction(status)
  }

  useEffect(() => {
    if (specialCertificationsStatus === 'uninitialized') {
      dispatch(fetchSpecialCertifications())
    }
  }, [specialCertificationsStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  return (
    <>
      <Typography variant="h1">Special Certifications</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 2, columnGap: 3 }}>
        <Box sx={{ flex: '1 0 320px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              sx={{ mb: 1.5 }}
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => onSelection('create', '')}
            >
              Create Special Certification
            </Button>
          </Box>
          {specialCertifications.map((sc) => (
            <Box
              key={sc.id}
              sx={{
                px: 2,
                py: 1,
                mb: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: `1px solid ${theme.palette.text.secondary}`,
                borderRadius: 1,
                '&:hover': {
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                },
              }}
              onClick={() => onSelection('view', sc.id)}
            >
              <Typography>{sc.name}</Typography>
              <Typography color="text.secondary">
                {sc.courseIDs?.length || 'No'} Courses
              </Typography>
            </Box>
          ))}
          <Box sx={{ mt: -1 }}>
            {specialCertifications.length ? (
              <Pagination
                size="small"
                sx={{ py: '5px', display: 'flex', justifyContent: 'center' }}
                count={Math.ceil(specialCertifications.length / 10)}
                page={page}
                onChange={(e: React.ChangeEvent<unknown>, value: number) => {
                  setPage(value)
                }}
                showFirstButton
                showLastButton
              />
            ) : (
              <Typography>
                There are no special certifications to display.
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: '1 0 320px' }}>
          <form onSubmit={form.handleSubmit}>
            {selected || action === 'create' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ textAlign: 'center' }} variant="h5">
                  {action === 'create'
                    ? 'Create Special Certification'
                    : selectedCert?.name}
                </Typography>
                {action !== 'create' && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      mt: -1,
                    }}
                  >
                    {action === 'view' || action === 'delete' ? (
                      <Button
                        sx={{ flex: '0 0 120px' }}
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() =>
                          onSelection('edit', selectedCert?.id || '')
                        }
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button
                        sx={{ flex: '0 0 120px' }}
                        variant="outlined"
                        color="primary"
                        startIcon={<PreviewIcon />}
                        onClick={() =>
                          onSelection('view', selectedCert?.id || '')
                        }
                      >
                        View
                      </Button>
                    )}
                    <Button
                      sx={{ flex: '0 0 120px' }}
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setAction('delete')
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
                {(action === 'create' || action === 'edit') && (
                  <TextField
                    variant="outlined"
                    size="medium"
                    label="Name"
                    name="name"
                    type="text"
                    fullWidth
                    value={form.values.name}
                    onChange={form.handleChange}
                    error={form.touched.name && !!form.errors.name}
                  />
                )}
                <Box sx={{ mt: -1, mb: -3 }}>
                  <EntityList
                    entities={
                      action === 'create' || action === 'edit'
                        ? courses.filter((c) => !c.linkedServiceID)
                        : courses.filter((c) =>
                            selectedCert?.courseIDs.includes(c.id),
                          )
                    }
                    checkbox={
                      action === 'create' || action === 'edit'
                        ? {
                            checked: checkedCourses,
                            onClick: setCheckedCourses,
                          }
                        : undefined
                    }
                    info={{ card: CourseCard }}
                  />
                </Box>
                {(action === 'create' || action === 'edit') && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      sx={{ flex: '0 0 120px' }}
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      startIcon={<SubmitIcon />}
                    >
                      {action === 'create' ? 'Create' : 'Update'}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography sx={{ textAlign: 'center' }}>
                Select a special certification to view details or create a new
                one.
              </Typography>
            )}
          </form>
        </Box>
      </Box>
      <ConfirmAction
        executeAction={() =>
          axios
            .delete(
              `/api/v2/admin/special-certification?id=${selectedCert?.id}`,
            )
            .then(() => setSelected(''))
            .then(() => dispatch(fetchSpecialCertifications()))
        }
        open={action}
        setOpen={(value: typeof action) => {
          setAction(!value ? 'view' : value)
        }}
        openText="delete"
        title="Confirm Deletion"
        text={`Are you sure you want to delete ${selectedCert?.name}?`}
        onFailure={(err) =>
          setErrorMessage(err.message || 'Error Deleting Special Certification')
        }
      />
      <Backdrop open={saving} onClick={() => setSaving(false)}>
        <CircularProgress />
      </Backdrop>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
